import { useState, useRef, useEffect, useCallback } from 'react';
import { useMyTenants } from '../hooks/useMyTenants';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: Source[];
  confidence?: number;
  isError?: boolean;
}

interface Source {
  title: string;
  content: string;
  score: number;
}

interface Tenant {
  id: string;
  name: string;
  widget_key: string;
  api_key: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function ChatTestPage() {
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // [2026-03-22] 역할별 테넌트 목록
  const { tenants } = useMyTenants();

  // 세션 ID 생성
  useEffect(() => {
    setSessionId(generateSessionId());
  }, [selectedTenant]);

  // 메시지 스크롤
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateSessionId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading || !selectedTenant) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/v1/widget/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Key': selectedTenant.widget_key,
        },
        body: JSON.stringify({
          message: inputValue,
          session_id: sessionId,
          include_sources: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage: Message = {
          id: data.message_id,
          role: 'assistant',
          content: data.answer,
          timestamp: new Date(),
          sources: data.sources?.map((s: any) => ({
            title: s.document_title,
            content: s.chunk_content,
            score: s.similarity_score,
          })),
          confidence: data.confidence_score,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: errorData.detail || '요청 처리 중 오류가 발생했습니다.',
          timestamp: new Date(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '네트워크 오류가 발생했습니다. 서버 연결을 확인해주세요.',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, selectedTenant, sessionId]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTenantChange = (tenantId: string) => {
    const tenant = tenants.find((t: Tenant) => t.id === tenantId);
    setSelectedTenant(tenant || null);
    setMessages([]);
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(generateSessionId());
  };

  return (
    <div className="h-full flex flex-col">
      {/* 헤더 */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">채팅 테스트</h1>
            <p className="text-gray-600">테넌트 챗봇을 테스트합니다</p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={selectedTenant?.id || ''}
              onChange={(e) => handleTenantChange(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">테넌트 선택...</option>
              {tenants.map((tenant: Tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
            {selectedTenant && (
              <button
                onClick={clearChat}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              >
                대화 초기화
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 채팅 영역 */}
      <div className="flex-1 flex overflow-hidden">
        {!selectedTenant ? (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <svg
                className="mx-auto h-16 w-16 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">테넌트를 선택하세요</h3>
              <p className="mt-2 text-gray-500">
                상단의 드롭다운에서 테스트할 테넌트를 선택하면<br />
                해당 챗봇과 대화를 시작할 수 있습니다.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col bg-gray-50">
            {/* 메시지 목록 */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-3xl mx-auto space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                      <svg
                        className="w-8 h-8 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {selectedTenant.name} 챗봇 테스트
                    </h3>
                    <p className="mt-2 text-gray-500">
                      메시지를 입력하여 챗봇을 테스트해보세요.
                    </p>
                  </div>
                )}

                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : message.isError
                          ? 'bg-red-100 text-red-800 border border-red-200'
                          : 'bg-white text-gray-800 shadow-sm border'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>

                      {/* 신뢰도 표시 */}
                      {message.role === 'assistant' && message.confidence !== undefined && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <span className="text-xs text-gray-500">
                            신뢰도: {(message.confidence * 100).toFixed(1)}%
                          </span>
                        </div>
                      )}

                      {/* 소스 표시 */}
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-500 mb-2">참조 문서:</p>
                          <div className="space-y-2">
                            {message.sources.map((source, idx) => (
                              <div
                                key={idx}
                                className="text-xs bg-gray-50 rounded-lg p-2"
                              >
                                <p className="font-medium text-gray-700">{source.title}</p>
                                <p className="text-gray-500 mt-1 line-clamp-2">
                                  {source.content}
                                </p>
                                <p className="text-gray-400 mt-1">
                                  유사도: {(source.score * 100).toFixed(1)}%
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* 입력 영역 */}
            <div className="bg-white border-t p-4">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="메시지를 입력하세요..."
                      rows={1}
                      className="w-full px-4 py-3 border rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      style={{ maxHeight: '120px' }}
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Enter로 전송, Shift+Enter로 줄바꿈
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
